import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    DatePicker,
    message,
    Select,
    Space,
    Spin,
    Steps,
    Table,
    Typography
} from 'antd';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import {
    CheckCircleOutlined,
    ImportOutlined,
    LinkOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { useAccountStore, useUserState } from '../../store';
import * as SnapTradeAPI from '../../utilities/snaptrade';

import './styles.scss';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

export default function SnapTradeImport({ onSuccess }) {
    const navigate = useNavigate();
    const { getUser } = useUserState();
    const user = getUser();
    const { allAccounts } = useAccountStore((state) => state);

    const [currentStep, setCurrentStep] = useState(0);
    const [selectedSnapTradeAccount, setSelectedSnapTradeAccount] = useState(null);
    const [selectedJournalAccount, setSelectedJournalAccount] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
    const [previewData, setPreviewData] = useState(null);

    // Register user with SnapTrade on component mount
    useEffect(() => {
        if (user?.id) {
            SnapTradeAPI.registerUser(user.id).catch((err) => {
                message.error('Failed to register with SnapTrade');
                console.error('Failed to register:', err);
            });
        }
    }, [user?.id]);

    // Fetch brokerage accounts
    const {
        data: snapTradeAccounts,
        isLoading: loadingAccounts,
        refetch: refetchAccounts
    } = useQuery(
        ['snaptrade-accounts', user?.id],
        () => SnapTradeAPI.listAccounts(user.id).then((res) => res.data.accounts),
        { enabled: !!user?.id && currentStep >= 1 }
    );

    // Fetch brokerage connections
    const { data: connections, refetch: refetchConnections } = useQuery(
        ['snaptrade-connections', user?.id],
        () => SnapTradeAPI.listConnections(user.id).then((res) => res.data.connections),
        { enabled: !!user?.id }
    );

    // Generate connection URL
    const generateConnectionUrl = async () => {
        try {
            const response = await SnapTradeAPI.getConnectionUrl(user.id, window.location.origin);
            const { redirectURI } = response.data;
            // Open in new window
            const width = 600;
            const height = 700;
            const left = window.screen.width / 2 - width / 2;
            const top = window.screen.height / 2 - height / 2;
            window.open(
                redirectURI,
                'SnapTrade Connection',
                `width=${width},height=${height},left=${left},top=${top}`
            );
            message.info('Complete the connection in the popup window');
        } catch (error) {
            message.error('Failed to generate connection URL');
            console.error(error);
        }
    };

    // Preview trades mutation
    const previewMutation = useMutation((params) => SnapTradeAPI.previewActivities(params), {
        onSuccess: (response) => {
            setPreviewData(response.data);
            setCurrentStep(2);
            message.success(`Found ${response.data.tradesCount} trades`);
        },
        onError: (error) => {
            message.error(error.response?.data?.message || 'Failed to preview trades');
        }
    });

    // Import trades mutation
    const importMutation = useMutation((params) => SnapTradeAPI.importTrades(params), {
        onSuccess: (response) => {
            message.success(`Successfully imported ${response.data.tradesImported} trades!`);
            setCurrentStep(3);
            if (onSuccess) onSuccess(selectedJournalAccount);
        },
        onError: (error) => {
            message.error(error.response?.data?.message || 'Failed to import trades');
        }
    });

    const handleConnect = () => {
        generateConnectionUrl();
    };

    const handleRefreshConnections = () => {
        refetchConnections();
        refetchAccounts();
        message.info('Refreshing connections...');
    };

    const handleNext = () => {
        if (currentStep === 0) {
            if (!connections || connections.length === 0) {
                message.warning('Please connect a brokerage account first');
                return;
            }
            setCurrentStep(1);
        } else if (currentStep === 1) {
            if (!selectedSnapTradeAccount || !selectedJournalAccount || !dateRange) {
                message.warning('Please select account and date range');
                return;
            }
            // Preview trades
            previewMutation.mutate({
                userId: user.id,
                snaptradeAccountId: selectedSnapTradeAccount,
                journalAccountId: selectedJournalAccount,
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD')
            });
        } else if (currentStep === 2) {
            // Import trades
            importMutation.mutate({
                userId: user.id,
                snaptradeAccountId: selectedSnapTradeAccount,
                journalAccountId: selectedJournalAccount,
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD')
            });
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const tradeColumns = [
        {
            title: 'Symbol',
            dataIndex: ['trade', 'symbol'],
            key: 'symbol',
            width: 100
        },
        {
            title: 'Side',
            dataIndex: ['trade', 'side'],
            key: 'side',
            width: 80
        },
        {
            title: 'Open Time',
            dataIndex: ['trade', 'openTime'],
            key: 'openTime',
            width: 150,
            render: (time) => dayjs(time).format('MMM DD, YYYY HH:mm')
        },
        {
            title: 'Close Time',
            dataIndex: ['trade', 'closeTime'],
            key: 'closeTime',
            width: 150,
            render: (time) => (time ? dayjs(time).format('MMM DD, YYYY HH:mm') : 'Open')
        },
        {
            title: 'Volume',
            dataIndex: ['trade', 'totalVolume'],
            key: 'volume',
            width: 100
        },
        {
            title: 'Net P&L',
            dataIndex: ['trade', 'netPnL'],
            key: 'netPnL',
            width: 100,
            render: (value) => {
                const color = value >= 0 ? 'green' : 'red';
                return <Text style={{ color }}>${value?.toFixed(2) || '-'}</Text>;
            }
        }
    ];

    const steps = [
        {
            title: 'Connect',
            icon: <LinkOutlined />
        },
        {
            title: 'Select',
            icon: <ImportOutlined />
        },
        {
            title: 'Preview',
            icon: <CheckCircleOutlined />
        },
        {
            title: 'Done',
            icon: <CheckCircleOutlined />
        }
    ];

    const allAccountsFiltered = allAccounts?.filter(
        (acc) => !acc.pct_account && !acc.simulator_account && !acc.ptt_account && !acc.liveAccount
    );

    return (
        <div className="snaptrade-import">
            <Steps current={currentStep} items={steps} />

            {/* Step 0: Connect Brokerage */}
            {currentStep === 0 && (
                <Card bodyStyle={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Title level={5}>Connect Your Brokerage</Title>
                        <Text type="secondary">
                            Connect your brokerage account to automatically import trades into your
                            journal.
                        </Text>

                        {connections && connections.length > 0 ? (
                            <Alert
                                message="Brokerage Connected"
                                description={`You have ${connections.length} brokerage account(s) connected.`}
                                type="success"
                                showIcon
                                icon={<CheckCircleOutlined />}
                            />
                        ) : (
                            <Alert
                                message="No Brokerage Connected"
                                description="Click the button below to connect your brokerage account."
                                type="info"
                                showIcon
                            />
                        )}

                        <Space>
                            <Button type="primary" icon={<LinkOutlined />} onClick={handleConnect}>
                                Connect Brokerage
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleRefreshConnections}>
                                Refresh
                            </Button>
                        </Space>

                        {connections && connections.length > 0 && (
                            <div>
                                <Text strong>Connected Brokerages:</Text>
                                <div
                                    style={{ maxHeight: '200px', overflowY: 'auto', marginTop: 8 }}
                                >
                                    {connections.map((conn) => (
                                        <Card key={conn.id} size="small" style={{ marginTop: 8 }}>
                                            <Space>
                                                {conn.brokerage.aws_s3_logo_url && (
                                                    <img
                                                        src={conn.brokerage.aws_s3_logo_url}
                                                        alt={conn.brokerage.name}
                                                        style={{ width: 24, height: 24 }}
                                                    />
                                                )}
                                                <Text>{conn.brokerage.name}</Text>
                                                <Text type="secondary">
                                                    ({conn.name || 'Connected'})
                                                </Text>
                                            </Space>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Space>
                </Card>
            )}

            {/* Step 1: Select Account & Date Range */}
            {currentStep === 1 && (
                <Card bodyStyle={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Title level={5}>Select Account & Date Range</Title>

                        <div>
                            <Text strong>SnapTrade Brokerage Account:</Text>
                            <Select
                                style={{ width: '100%', marginTop: 8 }}
                                placeholder="Select brokerage account"
                                value={selectedSnapTradeAccount}
                                onChange={setSelectedSnapTradeAccount}
                                loading={loadingAccounts}
                            >
                                {snapTradeAccounts?.map((account) => (
                                    <Select.Option key={account.id} value={account.id}>
                                        {account.institution_name} - {account.name} (
                                        {account.number})
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <Text strong>Journal Account (to import into):</Text>
                            {allAccountsFiltered && allAccountsFiltered.length > 0 ? (
                                <Select
                                    style={{ width: '100%', marginTop: 8 }}
                                    placeholder="Select journal account"
                                    value={selectedJournalAccount}
                                    onChange={setSelectedJournalAccount}
                                >
                                    {allAccountsFiltered.map((account) => (
                                        <Select.Option key={account.key} value={account.key}>
                                            {account.label} ({account.brokerLabel})
                                        </Select.Option>
                                    ))}
                                </Select>
                            ) : (
                                <Button
                                    type="primary"
                                    style={{ width: '100%', marginTop: 8 }}
                                    onClick={() => navigate('/settings/1')}
                                >
                                    Create Journal Account
                                </Button>
                            )}
                        </div>

                        <div>
                            <Text strong>Date Range:</Text>
                            <RangePicker
                                style={{ width: '100%', marginTop: 8 }}
                                value={dateRange}
                                onChange={setDateRange}
                                format="YYYY-MM-DD"
                            />
                        </div>
                    </Space>
                </Card>
            )}

            {/* Step 2: Preview Trades */}
            {currentStep === 2 && previewData && (
                <Card bodyStyle={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Title level={5}>Preview Trades</Title>

                        <Table
                            columns={tradeColumns}
                            dataSource={previewData.trades}
                            rowKey={(record, index) => index}
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 800 }}
                            size="small"
                        />
                    </Space>
                </Card>
            )}

            {/* Step 3: Success */}
            {currentStep === 3 && (
                <Card bodyStyle={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%', textAlign: 'center' }}
                    >
                        <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
                        <Title level={4}>Import Successful!</Title>
                        <Text>Your trades have been imported to your journal.</Text>
                    </Space>
                </Card>
            )}

            {/* Action Buttons */}
            <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: '16px' }}>
                <Button onClick={handleBack} disabled={currentStep === 0 || currentStep === 3}>
                    Back
                </Button>
                {currentStep < 3 && (
                    <Button
                        type="primary"
                        onClick={handleNext}
                        disabled={currentStep === 3}
                        loading={previewMutation.isLoading || importMutation.isLoading}
                    >
                        {currentStep === 0 && 'Next'}
                        {currentStep === 1 && 'Preview Trades'}
                        {currentStep === 2 && 'Import Trades'}
                    </Button>
                )}
                {currentStep === 3 && (
                    <Button
                        type="primary"
                        onClick={() => {
                            navigate(`/trades?account=${selectedJournalAccount}`);
                        }}
                        loading={previewMutation.isLoading || importMutation.isLoading}
                    >
                        Done
                    </Button>
                )}
            </Space>

            {(previewMutation.isLoading || importMutation.isLoading) && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin size="large" />
                </div>
            )}
        </div>
    );
}

SnapTradeImport.propTypes = {
    onSuccess: PropTypes.func
};
